import React, { useState, useMemo, useCallback } from 'react';
import { Send, Megaphone, Clock, LogIn, HandHelping, ChevronDown, XCircle, Users, Pencil } from 'lucide-react';
import { useBreakoutRooms } from '../../../context/BreakoutRoomContext';

const ROOM_DOT_COLORS = [
  '#6a1fbf', '#f59e0b', '#00c853', '#ef4444', '#3b82f6',
  '#ec4899', '#14b8a6', '#f97316', '#8b5cf6', '#06b6d4',
];

const TIMER_OPTIONS = [
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
  { label: '15 min', value: 15 },
  { label: '20 min', value: 20 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '60 min', value: 60 },
];

function formatTimer(seconds) {
  if (seconds == null) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  const mins = Math.floor(diff / 60);
  return `${mins} min${mins > 1 ? 's' : ''} ago`;
}

export default function ManageTab() {
  const [messageInput, setMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedAll, setSelectedAll] = useState(true);
  const [selectedRoomIds, setSelectedRoomIds] = useState(new Set());

  const {
    rooms,
    isActive,
    timerRemaining,
    roomTimers,
    helpRequests,
    broadcastMessage,
    respondToHelp,
    joinRoom,
    currentBreakoutRoom,
    leaveRoom,
    closeRoom,
    setRoomTimer,
    renameRoom,
    session,
  } = useBreakoutRooms();

  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const startEditingRoom = useCallback((room) => {
    setEditingRoomId(room.roomId);
    setEditingName(room.roomName || '');
  }, []);

  const cancelEditingRoom = useCallback(() => {
    setEditingRoomId(null);
    setEditingName('');
  }, []);

  const commitEditingRoom = useCallback(() => {
    const id = editingRoomId;
    const trimmed = editingName.trim();
    if (!id) return;
    const current = (rooms || []).find(r => r.roomId === id);
    if (!current || !trimmed || trimmed === current.roomName) {
      cancelEditingRoom();
      return;
    }
    renameRoom(id, trimmed);
    cancelEditingRoom();
  }, [editingRoomId, editingName, rooms, renameRoom, cancelEditingRoom]);

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;
    // Build the targets list from the *active*-only selection so closed rooms
    // (filtered out of the picker but possibly still in selectedRoomIds before
    // the prune effect runs) cannot leak into the payload.
    const targets = selectedAll ? null : Array.from(activeSelectedRoomIds);
    if (!selectedAll && targets.length === 0) return;
    setSendingMessage(true);
    broadcastMessage(messageInput.trim(), targets);
    setMessageInput('');
    setTimeout(() => setSendingMessage(false), 1500);
  };

  const toggleRoom = (roomId) => {
    setSelectedRoomIds(prev => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
    setSelectedAll(false);
  };

  const toggleAll = () => {
    setSelectedAll(true);
    setSelectedRoomIds(new Set());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const settings = session?.settings || {};
  const individualTimers = settings.individualTimers;
  const defaultTimerMin = Math.round((settings.timerDuration || 900) / 60);

  const roomList = rooms || [];
  // Broadcast targets must exclude closed rooms — once a room's timer expires
  // the backend marks it isActive=false but keeps it in the session for the
  // "Active Rooms" column (which renders it greyed out). The broadcast picker
  // must NOT include closed rooms or messages will be sent to a dead room.
  const broadcastRoomList = roomList.filter(r => r.isActive !== false);
  const broadcastRoomIds = new Set(broadcastRoomList.map(r => r.roomId));
  // Drop any selection that points at a now-closed room so the count + send
  // payload stay in sync with what's actually visible.
  const activeSelectedRoomIds = useMemo(() => {
    const next = new Set();
    selectedRoomIds.forEach(id => { if (broadcastRoomIds.has(id)) next.add(id); });
    return next;
  }, [selectedRoomIds, broadcastRoomList]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasTargets = selectedAll || activeSelectedRoomIds.size > 0;
  const sendLabel = sendingMessage
    ? 'Sent!'
    : selectedAll
      ? 'Send to All'
      : activeSelectedRoomIds.size === 1
        ? 'Send to 1 Room'
        : `Send to ${activeSelectedRoomIds.size} Rooms`;

  if (!isActive) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px] md:min-h-[340px] text-center">
        <Users className="w-12 h-12 text-gray-600 mb-3" />
        <p className="text-gray-400 text-sm font-medium">No active rooms yet</p>
        <p className="text-gray-500 text-xs mt-1.5">Open breakout rooms from the Setup tab to get started.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 min-h-0 md:min-h-[340px]">

      {/* ── Column 1: Broadcast Message ── */}
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone className="w-4 h-4 text-[#6a1fbf]" />
          <h3 className="text-white text-sm font-semibold tracking-wide">Broadcast Message</h3>
        </div>

        <textarea
          name="rlink-chat-message"
          autoComplete="off"
          data-form-type="other"
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={3}
          className="w-full bg-[#0a1628] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#6a1fbf]/60 resize-none mb-4"
        />

        <p className="text-gray-400 text-xs font-medium mb-2">Send to:</p>

        <div className="border border-white/10 rounded-xl overflow-hidden mb-4 max-h-[30vh] md:max-h-none overflow-y-auto">
          {/* All Rooms */}
          <button
            onClick={toggleAll}
            className={`flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-left transition-colors ${
              selectedAll
                ? 'bg-[#6a1fbf] text-white'
                : 'bg-transparent text-gray-300 hover:bg-white/5'
            }`}
          >
            <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center text-[10px] ${
              selectedAll ? 'bg-white border-white text-[#6a1fbf]' : 'border-gray-500'
            }`}>
              {selectedAll && '✓'}
            </span>
            All Rooms
          </button>

          {/* Individual rooms — closed rooms are excluded from the broadcast picker */}
          {broadcastRoomList.map((room, i) => {
            const dotColor = ROOM_DOT_COLORS[i % ROOM_DOT_COLORS.length];
            const isChecked = selectedAll || activeSelectedRoomIds.has(room.roomId);
            return (
              <button
                key={room.roomId}
                onClick={() => toggleRoom(room.roomId)}
                className={`flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-left transition-colors border-t border-white/5 ${
                  !selectedAll && activeSelectedRoomIds.has(room.roomId)
                    ? 'bg-[#6a1fbf]/20 text-white'
                    : 'bg-transparent text-gray-300 hover:bg-white/5'
                }`}
              >
                <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center text-[10px] ${
                  isChecked ? 'bg-white border-white text-[#6a1fbf]' : 'border-gray-500'
                }`}>
                  {isChecked && '✓'}
                </span>
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: dotColor }}
                />
                {room.roomName}
              </button>
            );
          })}
        </div>

        <button
          onClick={handleSendMessage}
          disabled={!messageInput.trim() || sendingMessage || !hasTargets}
          className="flex items-center justify-center gap-2 w-full bg-[#0a1628] border border-white/10 text-gray-300 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed mt-auto"
        >
          <Send className="w-3.5 h-3.5" />
          {sendLabel}
        </button>
      </div>

      {/* ── Column 2: Active Rooms ── */}
      <div className="flex flex-col">
        <h3 className="text-white text-sm font-semibold tracking-wide mb-3">Active Rooms</h3>

        <div className="space-y-3 flex-1 overflow-y-auto pr-1 md:max-h-none">
            {(rooms || []).map((room, idx) => {
              const dotColor = ROOM_DOT_COLORS[idx % ROOM_DOT_COLORS.length];
              const participantCount = (room.participants || []).length;
              const isInThisRoom = currentBreakoutRoom?.roomId === room.roomId;
              const isRoomClosed = room.isActive === false;

              // Per-room timer: use roomTimers from socket if available, else fall back to global
              const roomRemaining = individualTimers
                ? (roomTimers?.[room.roomId] ?? null)
                : timerRemaining;
              const roomDurationMin = room.timerDuration
                ? Math.round(room.timerDuration / 60)
                : defaultTimerMin;

              return (
                <div
                  key={room.roomId}
                  className={`border rounded-xl p-4 ${isRoomClosed ? 'border-white/5 bg-[#0a1628]/30 opacity-60' : 'border-white/10 bg-[#0a1628]/50'}`}
                >
                  {/* Room name + count + close */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: isRoomClosed ? '#6b7280' : dotColor }}
                      />
                      {editingRoomId === room.roomId ? (
                        <input
                          autoFocus
                          type="text"
                          value={editingName}
                          maxLength={60}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={commitEditingRoom}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commitEditingRoom(); }
                            else if (e.key === 'Escape') { e.preventDefault(); cancelEditingRoom(); }
                          }}
                          className="bg-white/10 border border-indigo-400/60 focus:border-indigo-400 focus:outline-none text-white text-sm font-semibold rounded px-1.5 py-0.5 min-w-0"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => !isRoomClosed && startEditingRoom(room)}
                          disabled={isRoomClosed}
                          title={isRoomClosed ? '' : 'Click to rename'}
                          className={`group flex items-center gap-1 min-w-0 text-left rounded px-1 -mx-1 py-0.5 transition-colors ${
                            isRoomClosed ? 'cursor-default' : 'hover:bg-white/5'
                          }`}
                        >
                          <span className={`text-sm font-semibold ${isRoomClosed ? 'text-gray-500' : 'text-white'}`}>{room.roomName}</span>
                          {!isRoomClosed && (
                            <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          )}
                        </button>
                      )}
                      {isRoomClosed && (
                        <span className="text-[10px] text-red-400/80 font-medium uppercase tracking-wide">Closed</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs tabular-nums">{participantCount}</span>
                      {!isRoomClosed && (
                        <button
                          onClick={() => closeRoom(room.roomId)}
                          className="p-1 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400 transition-colors"
                          title="Close this room"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Join / Leave button */}
                  {isRoomClosed ? (
                    <div className="w-full bg-white/5 text-gray-500 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 mb-3 cursor-not-allowed">
                      <XCircle className="w-4 h-4" />
                      Room Closed
                    </div>
                  ) : isInThisRoom ? (
                    <button
                      onClick={() => leaveRoom()}
                      className="w-full bg-red-500/20 text-red-400 border border-red-500/30 py-2 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors flex items-center justify-center gap-2 mb-3"
                    >
                      <LogIn className="w-4 h-4 rotate-180" />
                      Leave Room
                    </button>
                  ) : (
                    <button
                      onClick={() => joinRoom(room.roomId, room.roomName)}
                      className="w-full bg-[#6a1fbf] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#7b2fcf] transition-colors flex items-center justify-center gap-2 mb-3"
                    >
                      <LogIn className="w-4 h-4" />
                      Join Room
                    </button>
                  )}

                  {/* Timer — hidden for closed rooms */}
                  {settings.timerEnabled && !isRoomClosed && (
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-3 h-3 text-gray-500" />
                        <span className="text-gray-400 text-[11px]">Timer:</span>
                        {roomRemaining != null && roomRemaining >= 0 && (
                          <span className={`text-[11px] font-mono ml-auto ${
                            roomRemaining <= 60 ? 'text-red-400 animate-pulse' :
                            roomRemaining <= 300 ? 'text-[#ffa500]' : 'text-[#00c853]'
                          }`}>
                            {formatTimer(roomRemaining)} left
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <select
                          value={individualTimers ? roomDurationMin : defaultTimerMin}
                          onChange={(e) => {
                            if (individualTimers) {
                              setRoomTimer(room.roomId, parseInt(e.target.value));
                            }
                          }}
                          disabled={!individualTimers}
                          className={`w-full bg-[#0a1628] border border-white/10 rounded-lg px-3 py-2 text-white text-xs appearance-none cursor-pointer focus:outline-none focus:border-[#6a1fbf]/50 pr-8 ${
                            !individualTimers ? 'opacity-70 cursor-default' : ''
                          }`}
                        >
                          {TIMER_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value} className="bg-[#001233]">
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-gray-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
      </div>

      {/* ── Column 3: Help Requests ── */}
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <HandHelping className="w-4 h-4 text-[#ffa500]" />
          <h3 className="text-white text-sm font-semibold tracking-wide">Help Requests</h3>
        </div>

        {helpRequests.length === 0 ? (
          <div className="border border-white/10 rounded-xl p-8 text-center flex-1 flex flex-col items-center justify-center bg-[#0a1628]/50">
            <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center mb-3">
              <HandHelping className="w-7 h-7 text-gray-600" />
            </div>
            <p className="text-gray-400 text-sm font-medium">No help requests</p>
            <p className="text-gray-500 text-xs mt-1.5 leading-relaxed max-w-[200px]">
              Participants can request assistance from their breakout room
            </p>
          </div>
        ) : (
          <div className="space-y-2 flex-1 overflow-y-auto pr-1">
            {helpRequests.map((req, index) => {
              const room = (rooms || []).find(r => r.roomId === req.roomId);
              return (
                <div
                  key={`${req.odId}-${index}`}
                  className="bg-red-500/10 border border-red-500/20 rounded-xl p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mt-1.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium break-words">{req.odName}</p>
                        <p className="text-gray-400 text-xs">{room?.roomName || req.roomId}</p>
                        <p className="text-gray-500 text-[10px] mt-0.5">{timeAgo(req.requestedAt)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        respondToHelp(req.roomId, req.odId);
                        if (room) joinRoom(room.roomId, room.roomName);
                      }}
                      className="bg-[#6a1fbf] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#7b2fcf] transition-colors flex items-center gap-1.5 flex-shrink-0"
                    >
                      <LogIn className="w-3 h-3" />
                      Join
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
